import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar, Toolbar, Typography, Box, Container, Button, Stack, IconButton, useMediaQuery, useTheme,
  Avatar, Menu, MenuItem, ListItemIcon, Divider, Snackbar, Alert,
} from '@mui/material';
import { Store as StoreIcon, Search as SearchIcon, Logout as LogoutIcon, Person as PersonIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const ezpPalette = {
  primary: '#1B5E20',
  secondary: '#FF6F00',
  bg: '#FAFAF5',
  bgDark: '#1A1A1A',
  text: '#212121',
  textMuted: '#757575',
  accent: '#4CAF50',
  border: '#E0E0E0',
};

export { ezpPalette };

export default function EzpLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('ezp');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, isAuthenticated, loginWithGoogle, logout } = useAuth();

  const googleInitialized = useRef(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Google One Tap credential callback
  const handleGoogleCredential = useCallback(async (response) => {
    if (!response.credential) return;
    try {
      const result = await loginWithGoogle(response.credential);
      if (result.success) {
        setSnackbar({ open: true, message: `歡迎，${result.user.name}！`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.error || '登入失敗', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: '登入失敗，請稍後再試', severity: 'error' });
    }
  }, [loginWithGoogle]);

  // Initialize Google One Tap
  useEffect(() => {
    if (isAuthenticated || googleInitialized.current) return;

    const initOneTap = async () => {
      // Fetch auth config to get client ID
      let clientId = '';
      try {
        const res = await api.get('/settings/auth/public');
        if (res.data.success && res.data.config.google_enabled) {
          clientId = res.data.config.google_client_id;
        }
      } catch (err) {
        // Fallback: use env variable
        clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
      }

      if (!clientId) return;

      const doInit = () => {
        if (!window.google?.accounts?.id || googleInitialized.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCredential,
          auto_select: true,
          cancel_on_tap_outside: false,
        });
        // Trigger One Tap auto-display prompt
        window.google.accounts.id.prompt();
        googleInitialized.current = true;
      };

      if (window.google?.accounts?.id) {
        doInit();
      } else {
        // Wait for GSI script to load
        const check = setInterval(() => {
          if (window.google?.accounts?.id) {
            clearInterval(check);
            doInit();
          }
        }, 200);
        // Stop checking after 10s
        setTimeout(() => clearInterval(check), 10000);
      }
    };

    initOneTap();
  }, [isAuthenticated, handleGoogleCredential]);

  // Cancel One Tap when user logs in
  useEffect(() => {
    if (isAuthenticated && window.google?.accounts?.id) {
      window.google.accounts.id.cancel();
    }
  }, [isAuthenticated]);

  const handleUserMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleUserMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    googleInitialized.current = false;
    // Re-show One Tap after logout
    if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt();
    }
  };

  const navItems = [
    { label: t('nav.products', '商品瀏覽'), path: '/products' },
    { label: t('nav.about', '合作模式'), path: '/partner' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: ezpPalette.bg, display: 'flex', flexDirection: 'column' }}>
      {/* Navbar */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: `1px solid ${ezpPalette.border}` }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: 64 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mr: 4 }} onClick={() => navigate('/')}>
              <StoreIcon sx={{ color: ezpPalette.primary, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 900, color: ezpPalette.primary, letterSpacing: -0.5 }}>
                EZP
              </Typography>
              <Typography variant="body2" sx={{ color: ezpPalette.textMuted, display: { xs: 'none', sm: 'block' } }}>
                {t('nav.tagline', '好批供貨站')}
              </Typography>
            </Box>

            {!isMobile && (
              <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
                {navItems.map(item => (
                  <Button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    sx={{
                      color: location.pathname === item.path ? ezpPalette.primary : ezpPalette.text,
                      fontWeight: location.pathname === item.path ? 700 : 500,
                      '&:hover': { bgcolor: 'rgba(76,175,80,0.08)' },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
            )}

            <Stack direction="row" spacing={1} sx={{ ml: 'auto', alignItems: 'center' }}>
              <IconButton onClick={() => navigate('/products')} sx={{ color: ezpPalette.text }}>
                <SearchIcon />
              </IconButton>

              {isAuthenticated && user ? (
                <>
                  <IconButton onClick={handleUserMenuOpen} sx={{ p: 0.5 }}>
                    <Avatar
                      src={user.avatar}
                      alt={user.name}
                      sx={{ width: 32, height: 32, bgcolor: ezpPalette.primary, fontSize: 14 }}
                    >
                      {(user.name || user.email || '?')[0]}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleUserMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem disabled sx={{ opacity: '1 !important' }}>
                      <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                      <Typography variant="body2" fontWeight={600}>{user.name || user.email}</Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                      登出
                    </MenuItem>
                  </Menu>
                </>
              ) : null}

              <Button
                variant="contained"
                size="small"
                href="https://urstore.tw/apply"
                target="_blank"
                sx={{
                  bgcolor: ezpPalette.secondary, fontWeight: 700, borderRadius: 2, textTransform: 'none',
                  '&:hover': { bgcolor: '#E65100' },
                }}
              >
                {t('nav.open_store', '免費開店')}
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main content */}
      <Box sx={{ flex: 1 }}>
        {children}
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: ezpPalette.bgDark, color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                EZP {t('footer.supply_station', '好批供貨站')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {t('footer.desc', '2,700+ 商品，一鍵上架你的網路商店')}
              </Typography>
            </Box>
            <Stack direction="row" spacing={3}>
              <Typography variant="body2" component="a" href="https://urstore.tw" target="_blank" sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', '&:hover': { color: 'white' } }}>
                URStore SaaS
              </Typography>
              <Typography variant="body2" component="a" href="https://www.ezp.com.tw" target="_blank" sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', '&:hover': { color: 'white' } }}>
                EZP {t('footer.official', '官網')}
              </Typography>
            </Stack>
          </Stack>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mt: 3 }}>
            © {new Date().getFullYear()} URStore. All rights reserved.
          </Typography>
        </Container>
      </Box>
      {/* Login feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
